<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="true"
                title="Payroll Adjustment List"
                btnTitle="Add New"
                :showAddNew="true"
                @onClickAddNewButton="onClickAddNewButton"
                @onClickCloseButton="onClickCloseButton"
            />
        </div>

      <div class="my-2 px-2">
        <div class="row">
          <div class="col-12 col-sm-6 col-md-4 col-lg-3">
            <div class="d-flex align-items-center justify-content-between filter-label">
              <label for="colFormLabel" class="col-form-label">Employee: </label>
              <span class="filter-search-icon cursor-pointer"
                    @click="onOpenContactSearchModal">
               <i class="fas fa-search"></i>
             </span>
            </div>

            <AsyncSelect
                placeholder="Select Employee"
                v-model="contactProfile"
                :api-service="fetchContactProfiles"
                :additional-query="additionalContactOption"
            />
          </div>
          <div class="col-2 d-flex align-items-end">
            <button
                style="min-width: 64px;"
                @click="getPayrollAdjustmentData"
                class="btn btn-primary btn-block waves-effect waves-float waves-light"
            >Go
            </button>
          </div>
        </div>
      </div>

       <div class="col-12 px-2 mt-2 mb-4">
          <div class="card" style="min-height: 200px">
            <div class="table-responsive">
              <table class="table">
                <thead>
                <tr>
                  <th>SL</th>
                  <th>Payroll Start Date</th>
                  <th>Payroll End Date</th>
                  <th>Employee Name</th>
                  <th>Pay Head</th>
                  <th>Pay Head Type</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in tableItems.data" :key="i">
                  <td>{{ i + 1 + ((tableItems.current_page - 1) * parseInt(tableItems.per_page)) }}</td>
                  <td>{{ item.payroll_start_date }}</td>
                  <td>{{ item.payroll_end_date }}</td>
                  <td>{{ item.employee_name + '(' + item.serial_no + ')' }}</td>
                  <td>{{ item.pay_head_name }}</td>
                  <td class="text-capitalize">{{ item.pay_head_type }}</td>
                  <td class="text-right">{{ item.pay_head_amount }}</td>
                  <td class="text-center">
                    <button class="btn btn-primary btn-sm"
                            @click="openAddEditModal(item)"
                            style="cursor: pointer"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
              <p v-if="tableItems.data && tableItems.data.length < 0" class="text-center mt-5">No data</p>
            </div>
          </div>
        </div>

      <div class="px-2 position-absolute bottom-0 mt-4">
        <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
      </div>

        <Loader v-if="loading"/>

        <PayrollAdjustmentModal
            v-if="store.state.isModalOpenTwo"
            :formData="formData"
            @submitted="getPayrollAdjustmentData"
        />

        <ContactDropdownFilter
            v-if="store.state.isModalOpenThree"
            @onSearchContact="onSearchContact"
            :companyRoles="companyRoles"
            :isFilterable="true"
            :roleSlug="'employee'"
        />

    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import { computed } from 'vue';
import PayrollAdjustmentModal from "@/components/molecule/company/hrm-payroll/payroll/PayrollAdjustmentModal.vue";
import figureFormatter from "@/services/utils/figureFormatter";
import Pagination from '@/components/atom/Pagination'
import handleRole from "@/services/modules/role";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from "@/services/modules/contact";
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const tableItems = ref([]);
const salaryStructureData = ref([]);
const params = ref({
  offset: 10,
});
const companyRoles = ref(null);
const additionalDealerQuery = ref({});
const additionalContactOption = ref({
  roles: 'employee'
});
const { commaFormat } = figureFormatter();
let contactProfile = ref(null)
const { fetchCompanyDefaultRoles } = handleRole();
const { fetchContactProfiles } = handleContact();
const token = computed(() => localStorage.getItem('token'));
const payrollMasterId = computed(() => route.params.payrollMasterId);

const formData = ref({
  payroll_master_id: payrollMasterId.value,
  contact_profile_id: '',
  pay_head_id: null,
  pay_head_amount: null,
  is_edit: false,
  type: 'adjustment',
})

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const getCompanyRoles = async () => {
  const res = await fetchCompanyDefaultRoles(`?company_id=${route.params.companyId}&roles=employee`);
  if (res.status) {
    companyRoles.value = res.data
  }
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true;
}

const onSearchContact = (contact) => {
  contactProfile.value = contact
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await getPayrollAdjustmentData();
}

const {
  fetchSinglePayrollAdjustment,
  fetchPayrollAdjustment
} = handleHrPayroll()

function onClickAddNewButton() {
  formData.value.contact_profile_id = null;
  formData.value.pay_head_id = null;
  formData.value.pay_head_amount = null;
  formData.value.is_edit = false;
  store.state.isModalOpenTwo = true
}

const onClickCloseButton = () => {
  router.push({name: `payroll-process`, params: route.params, query: route.query});
}

const openAddEditModal = (item) => {
  let query = `?company_id=${route.params.companyId}`;
  salaryStructureData.value = []
  fetchSinglePayrollAdjustment(item.id, query).then(res => {
    if (!res.status){
      return
    }
    let data = res.data;

    formData.value.id = data.id;
    formData.value.contact_profile_id = data.contact_profile_id;
    formData.value.employee_name = data.employee_name;
    formData.value.pay_head_amount = data.pay_head_amount;
    formData.value.pay_head_id = data.pay_head_id;
    formData.value.pay_head_name = data.pay_head_name;
    formData.value.is_edit = true;

    store.state.isModalOpenTwo = true
  });
}

const getPayrollAdjustmentData = async () => {
  let query = getQuery();
  loading.value = true
  tableItems.value = []

  await fetchPayrollAdjustment(query).then(res => {
    if (!res.status){
      return
    }

    tableItems.value = res.data;
    setPagination(res.data)
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}&payroll_master_id=${payrollMasterId.value}`;
  if (route.query.page) query += '&page=' + route.query.page;
  if (contactProfile.value) query += '&contact_profile_id=' + contactProfile.value.id;
  query += '&offset=' + params.value.offset
  query += `&type=adjustment`
  return query;
}

onMounted(() => {
    Promise.all([
      getPayrollAdjustmentData(),
      getCompanyRoles()
    ])
})
</script>
